// Client-side PDF export for a Selection Template / the Selection Management catalog, styled as a
// landscape 16:9 slide deck matching the company's existing "Gold Inclusions" reference
// presentations (cover slide + branded content slides, ≤8 items per slide). Follows the same
// render-hidden-HTML -> html2canvas -> jsPDF pattern already used for payment receipts and job
// schedule reports elsewhere in this app (see
// src/app/pages/jobs/schedules/components/SchedulesSharedUtils.js), but scoped to its own module
// rather than touching that working code.

import React from "react";
import { format } from "date-fns";
import { getAttachmentDataUri, DocumentUploadFeature } from "services/documentUploadService";
import { getCategoryLevelConfig } from "services/selectionCategoryLevelConfigService";
import { buildEffectiveLevels } from "./categoryLevelResolver";

export const formatDateTimeUTC = (dateStr) => {
  if (!dateStr) return null;
  try {
    const raw = String(dateStr).trim();
    if (!raw) return null;
    const hasTz = raw.endsWith("Z") || /[+-]\d{2}(:?\d{2})?$/.test(raw);
    const normalized = raw.includes("T") ? raw : raw.replace(" ", "T");
    const utcStr = hasTz ? normalized : `${normalized}Z`;
    const d = new Date(utcStr);
    if (isNaN(d.getTime())) {
      const fallback = new Date(raw);
      if (isNaN(fallback.getTime())) return null;
      return format(fallback, "dd/MM/yyyy hh:mm a");
    }
    return format(d, "dd/MM/yyyy hh:mm a");
  } catch {
    return null;
  }
};

const stripHtml = (html) => {
  if (!html) return "";
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/\s+/g, " ")
    .trim();
};

const truncateText = (text, max = 160) => {
  if (!text) return "";
  return text.length > max ? `${text.slice(0, max).trim()}…` : text;
};

// Groups a flat list of already-normalized print items (see mergeTemplateItemsWithOptions and
// buildCatalogExportData below) by Category, sorted within and across groups by displayOrder.
// Shared by both the Template export and the Selection Management catalog export.
const buildGroupsFromItems = (items) => {
  const groupsMap = new Map();
  items.forEach((item) => {
    const key = item.category || "";
    if (!groupsMap.has(key)) groupsMap.set(key, []);
    groupsMap.get(key).push(item);
  });

  return Array.from(groupsMap.entries())
    .map(([category, groupItems]) => {
      const sortedItems = [...groupItems].sort((a, b) => a.displayOrder - b.displayOrder);
      return { category, items: sortedItems, minOrder: sortedItems[0]?.displayOrder ?? 0 };
    })
    .sort((a, b) => {
      // Direct subcategories directly in main category (no Level 2 category)
      // must appear at the TOP, before any subcategories that belong to a category!
      const aIsDirect = !a.category || a.category === "Uncategorised" || a.category === "Direct Options";
      const bIsDirect = !b.category || b.category === "Uncategorised" || b.category === "Direct Options";
      if (aIsDirect && !bIsDirect) return -1;
      if (!aIsDirect && bIsDirect) return 1;
      return a.minOrder - b.minOrder;
    })
    .map(({ category, items: groupItems }) => ({ category, items: groupItems }));
};

// 4 columns x 2 rows, matching the density of the reference "Gold Inclusions" decks.
const MAX_ITEMS_PER_SLIDE = 8;

// Turns {category, items}[] into the flat slide list TemplatePdfPrintLayout renders. Each
// category is further split into one bucket per (Brand, Range) pair — matching how the reference
// decks give each Range its own slide(s), titled "{Range} Builders Range" (no price set on any of
// its items — a standard/included option) or "{Range} Upgrade Option" (at least one item priced),
// falling back to the Category name when an item has no Range. Brand is carried separately (not
// folded into the heading) so TemplatePdfPrintLayout can render it as a corner badge, matching the
// reference's supplier-logo placement. Reads the semantic `type`/`supplier`/`range` fields the
// three builder functions below all produce, rather than raw levelNName, so this bucketing logic
// doesn't need to know or care which physical tree level each one currently lives at.
export const buildSlidesFromGroups = (groups) => {
  const slides = [];

  (groups || []).forEach((group) => {
    const buckets = new Map();
    group.items.forEach((item) => {
      const type = item.type || "";
      const brand = item.supplier || "";
      const range = item.range || "";
      const key = `${type}||${brand}||${range}`;
      if (!buckets.has(key)) buckets.set(key, { type, brand, items: [] });
      buckets.get(key).items.push(item);
    });

    Array.from(buckets.values()).forEach(({ type, brand, items }) => {
      const groupName = items[0]?.range || group.category;
      const hasPrice = items.some((i) => i.price != null);
      const heading = `${groupName} ${hasPrice ? "Upgrade Option" : "Builders Range"}`;
      for (let i = 0; i < items.length; i += MAX_ITEMS_PER_SLIDE) {
        slides.push({ heading, type, brand, items: items.slice(i, i + MAX_ITEMS_PER_SLIDE) });
      }
    });
  });

  return slides;
};

// Merges a SelectionTemplate_GetById response (one thumbnailUrl per item) with the full master
// catalog option list (which carries the complete photos[]/notes per option) so the PDF can show
// every photo, not just one, then groups items by Category (level2Name) for section rendering.
export const mergeTemplateItemsWithOptions = (templateDetail, optionsById) => {
  const items = templateDetail?.items || [];

  const merged = items.map((item, index) => {
    const matched = optionsById.get(item.id);
    const photos = matched?.photos?.length > 0
      ? matched.photos
      : (item.thumbnailUrl ? [item.thumbnailUrl] : []);

    return {
      id: item.id,
      name: item.name || matched?.name || "Untitled",
      category: item.level2Name || matched?.category || "Uncategorised",
      mainCategoryName: item.level1Name || matched?.group || "",
      type: item.level3Name || matched?.type || "",
      supplier: item.level4Name || matched?.supplier || "",
      range: item.level5Name || matched?.range || "",
      productCode: item.productCode || matched?.productCode || "",
      hex: item.hexColor || matched?.hex || "",
      notes: truncateText(stripHtml(matched?.notes || matched?.comments || "")),
      photos,
      price: item.price ?? matched?.price ?? null,
      displayOrder: item.displayOrder ?? index,
    };
  });

  const groups = buildGroupsFromItems(merged);

  return {
    id: templateDetail?.id,
    name: templateDetail?.name || "Selection Template",
    description: templateDetail?.description || "",
    itemsCount: merged.length,
    categoriesCount: groups.length,
    groups,
  };
};

// Builds the same printable data shape directly from the master catalog's own option list
// (already-loaded "colour options" / displayedCards, in the legacy shape from
// mapApiOptionToLegacyShape), for exporting whatever's currently visible in Selection Management
// rather than a saved template's item list.
export const buildCatalogExportData = (options, { title, description } = {}) => {
  const merged = (options || []).map((opt, index) => ({
    id: opt.id,
    name: opt.colourName || opt.label || opt.name || "Untitled",
    category: opt.category || "Uncategorised",
    mainCategoryName: opt.group || opt.mainCategory || "",
    type: opt.type || "",
    supplier: opt.supplier || opt.company || "",
    range: opt.range || "",
    productCode: opt.productCode || "",
    hex: opt.hex || "",
    notes: truncateText(stripHtml(opt.notes || opt.comments || "")),
    photos: opt.photos || opt.images || [],
    price: opt.price ?? null,
    displayOrder: index,
  }));

  const groups = buildGroupsFromItems(merged);

  return {
    name: title || "Selection Catalog",
    description: description || "",
    itemsCount: merged.length,
    categoriesCount: groups.length,
    groups,
  };
};

// Plain-text, comma-joined list of an item's location tag names. Used for the standalone
// "Locations" row in both the on-page sign-off table and the PDF table, and by the PDF height
// estimate. Drops empty tag names so there are no stray ", ," fragments.
export const buildLocationsText = (item) =>
  (item?.locationTags || [])
    .map((t) => t.name || t.tagName)
    .filter(Boolean)
    .join(", ");

// Builds the printable data shape for the tabular "External/Internal Selection" job export format
// (SelectionsPage.jsx handleExportSelections/handleEmailClient) — one field-table section per
// Category (e.g. "Bricks", "Roof"), grouped under its CategoryType (External/Internal), matching
// the company's printed selection-sheet reference format. Selection Management/Template exports
// are unrelated and keep using the 16:9 slide deck via
// buildCatalogExportData/mergeTemplateItemsWithOptions below.
//
// `includeLocations` (default true) keeps the legacy behaviour of prefixing the comments with
// "Location(s): ...". Pass false when Locations is rendered as its own row.
//
// Dynamic/custom field values used to be folded into this same string (see buildDynamicFieldRows
// below for why that moved out) — this now only ever returns the real comment text someone typed
// when confirming the option, i.e. exactly item.notes/item.comments, nothing merged in.
export const buildRichComments = (item, { includeLocations = true } = {}) => {
  const parts = [];

  const locationText = buildLocationsText(item);
  if (includeLocations && locationText) {
    const prefix = (item.locationTags || []).length > 1 ? "Locations" : "Location";
    parts.push(
      React.createElement("span", { key: "loc" },
        React.createElement("strong", null, `${prefix}:`), ` ${locationText}`
      )
    );
  }

  const rawComments = item.notes || item.comments || "";
  const cleanComments = stripHtml(rawComments).trim();
  if (cleanComments) {
    parts.push(React.createElement("span", { key: "comments" }, cleanComments));
  }

  if (parts.length === 0) return "";

  return parts.reduce((acc, curr, i) => {
    if (i === 0) return [curr];
    return [...acc, React.createElement("span", { key: `sep-${i}` }, " , "), curr];
  }, []);
};

// Resolves one dynamic/custom field's display value the same way buildRichComments used to
// (attachments -> file count, single/multi-select -> matched option label, everything else ->
// the raw value verbatim).
const resolveDynamicFieldDisplayValue = (field) => {
  const rawValue = field.fieldValue || field.value || "";
  const htmlType = (field.htmlInputType || "").toLowerCase();

  if (htmlType === "attachments" || htmlType === "file") {
    try {
      const files = JSON.parse(rawValue || "[]");
      return Array.isArray(files) && files.length > 0
        ? `${files.length} file${files.length > 1 ? "s" : ""}`
        : "";
    } catch {
      return rawValue;
    }
  }

  if (field.options?.length > 0) {
    const match = field.options.find((o) => String(o.optionValue) === String(rawValue));
    return match?.optionLabel || rawValue;
  }

  return rawValue;
};

// Dynamic/custom fields answered against a confirmed option (job-level form fields on the
// category, not the Comments text box) — each one its own {label, value} row, rendered as a
// separate labelled row in both the on-page approval table and the exported PDF (previously these
// were squeezed into the Comments row via buildRichComments, which made the real comment hard to
// find amongst field values). Empty-valued fields are dropped rather than shown as a blank row.
export const buildDynamicFieldRows = (item) => {
  const dynFields = item.customFields || item.dynamicFields || item.dynamicFieldValues || item.dynamicData || [];
  if (!Array.isArray(dynFields) || dynFields.length === 0) return [];

  return dynFields
    .map((field) => ({
      label: field.fieldLabel || field.fieldName || field.name || "Field",
      value: resolveDynamicFieldDisplayValue(field),
    }))
    .filter((row) => row.value !== null && row.value !== undefined && String(row.value).trim() !== "");
};

// Resolves each distinct Main Category found among `items` (via item.mainCategoryNodeId) to its
// real Level 4 / Level 5 alias -- the same per-Main-Category config CategoryHierarchyPicker's
// "Edit Selection Option" fields already read (SelectionCategoryLevelConfig, with
// buildEffectiveLevels' legacy Type/Brand/Range synthesis for pre-config Main Categories) -- so
// the exported PDF labels those rows the way Selection Management actually names them instead of
// the old hardcoded "Brand"/"Range" strings. Returns Map<mainCategoryNodeId, Map<levelNumber,
// alias>>; a Main Category absent from the result (no id, fetch failure) just falls back to the
// hardcoded label in buildJobSelectionTableExportData below.
export const resolveLevelAliasesByMainCategory = async (items, flatNodes) => {
  const mainCategoryIds = Array.from(
    new Set((items || []).map((item) => item.mainCategoryNodeId).filter(Boolean))
  );

  const entries = await Promise.all(
    mainCategoryIds.map(async (mainCategoryNodeId) => {
      const res = await getCategoryLevelConfig(mainCategoryNodeId);
      const configRows = res.success ? res.data : [];
      const effectiveLevels = buildEffectiveLevels(configRows, flatNodes, mainCategoryNodeId);
      const levelMap = new Map(effectiveLevels.map((l) => [l.levelNumber, l.alias]));
      return [mainCategoryNodeId, levelMap];
    })
  );

  return new Map(entries);
};

export const buildJobSelectionTableExportData = (selections, { title, isLockedPdf, signatureDataUrl, approvalDate, showCounts, levelAliasesByMainCategory } = {}) => {
  const byCategoryType = new Map();

  (selections || []).forEach((item, index) => {
    const categoryType = item.categoryType || "External";
    if (!byCategoryType.has(categoryType)) byCategoryType.set(categoryType, []);

    // Estimate height for dynamic pagination with compact 22px rows
    const rawComments = item.notes || item.comments || "";
    const cleanComments = stripHtml(rawComments).trim();

    // Locations and Dynamic Fields each render in their own row (not inside Comments)
    const locText = buildLocationsText(item);
    const dynamicFieldRows = buildDynamicFieldRows(item);

    const extraLines = Math.max(0, Math.ceil(cleanComments.length / 60) - 2);
    const locationLines = locText ? Math.max(1, Math.ceil(locText.length / 40)) : 0;
    const dynamicFieldLines = dynamicFieldRows.reduce(
      (sum, row) => sum + Math.max(1, Math.ceil(String(row.value).length / 40)),
      0,
    );

    const visibleRowCount = Math.max(4,
      1 + // Name
      (item.brand || item.supplier ? 1 : 0) +
      (item.range ? 1 : 0) +
      (locText ? locationLines : 0) +
      dynamicFieldLines +
      1 + extraLines + // Comments
      (item.isLocked && item.lockedAt ? 1 : 0)
    );
    const subCategoryBarHeight = item.type ? 26 : 0;
    const estimatedHeight = (visibleRowCount * 22) + subCategoryBarHeight + 12;

    const itemLevelMap = levelAliasesByMainCategory?.get(item.mainCategoryNodeId);

    byCategoryType.get(categoryType).push({
      id: item.id,
      category: item.category || "",
      type: item.type || "",
      brand: item.supplier || "",
      brandLabel: itemLevelMap?.get(4) || "Brand",
      range: item.range || "",
      rangeLabel: itemLevelMap?.get(5) || "Range",
      option: item.name || "",
      locations: locText,
      dynamicFields: dynamicFieldRows,
      comments: cleanComments,
      isLocked: !!item.isLocked,
      isConfirmed: (item.status || "").toLowerCase().includes("confirmed") || item.isClientConfirmed || false,
      lockedDate: (item.isLocked && item.lockedAt) ? formatDateTimeUTC(item.lockedAt) : null,
      // Use the first image (DisplayOrder-ascending), rather than the last one, to match user expectations.
      photo: item.images?.length > 0 ? item.images[0] : null,
      displayOrder: index,
      estimatedHeight,
    });
  });

  const categoryTypes = Array.from(byCategoryType.entries()).map(([categoryType, items]) => ({
    categoryType,
    sections: buildGroupsFromItems(items),
  }));

  const formattedApprovalDate = approvalDate ? formatDateTimeUTC(approvalDate) : null;

  return {
    name: title || "Job Selections",
    categoryTypes,
    isLockedPdf: !!isLockedPdf,
    // Only present on the client-approval PDF's initial (unlocked) export — see
    // ClientSelectionApprovalPage's submit flow and JobSelectionTablePrintLayout's Footer, which
    // renders these in place of the blank "Client Initial / Date" prompt labels.
    signatureDataUrl: signatureDataUrl || null,
    approvalDateText: formattedApprovalDate,
    showCounts: !!showCounts,
  };
};

// A presigned/plain S3 URL is just `https://{bucket}.s3.{region}.amazonaws.com/{key}?{signature}` —
// the key is the pathname, so this recovers it regardless of query string or which S3-backed
// domain served it (matches S3FileStorageService.GetKeyFromUrl's server-side counterpart).
const extractS3KeyFromUrl = (url) => {
  try {
    return decodeURIComponent(new URL(url).pathname.replace(/^\/+/, "")) || null;
  } catch {
    return null;
  }
};

const resolvePhotoKey = async (key) => {
  if (!key) return null;
  if (key.startsWith("data:")) {
    return key;
  }
  // blob: URLs (freshly-picked local files, not yet uploaded) are same-origin to this page and
  // safe for html2canvas to read directly — no conversion needed.
  if (key.startsWith("blob:")) {
    return key;
  }
  // A presigned/plain URL still needs to become a data URI here — html2canvas's canvas capture
  // needs actual CORS cooperation from wherever it's hosted to read pixels from it, which a
  // plain <img> display doesn't require, so a URL that displays fine in-app can still render
  // blank in the exported PDF (see getAttachmentDataUri's doc comment for the full story). So
  // recover the underlying S3 key and route it through the same same-origin download used for
  // bare keys below, rather than handing the URL to html2canvas unconverted.
  const fileKey = /^https?:\/\//i.test(key) ? extractS3KeyFromUrl(key) : key;
  if (!fileKey) return null;
  // Keys follow `selection-option-docs/{companyId}/...` (see MediaFeatureFolder in
  // ClientSelectionApprovalService.cs). The anonymous client-selection-approval "thank you" page
  // has no JWT, so the backend can't infer the caller's company from the token the way it does for
  // logged-in users — pull it out of the key itself instead so /DocumentUpload/Download can build
  // the right folder prefix either way.
  const companyId = fileKey.split("/")[1] || null;
  const res = await getAttachmentDataUri(fileKey, companyId, DocumentUploadFeature.SELECTION_OPTION_ATTACHMENT);
  return res.success ? res.dataUri : null;
};

// Selection option photos are stored as raw S3 keys, not browsable URLs (see
// SelectionMediaImage.jsx) — the on-demand per-<img> resolution used elsewhere in the app
// doesn't work for a PDF export, since html2canvas snapshots the DOM synchronously and would
// capture the loading placeholder instead of the real photo. So every photo is resolved to a
// real data URI up front, before the printable layout ever mounts — a data URI is guaranteed
// safe for html2canvas's canvas capture regardless of the storage bucket's CORS configuration.
export const resolveExportPhotoUrls = async (mergedData) => {
  const groups = await Promise.all(
    mergedData.groups.map(async (group) => {
      const items = await Promise.all(
        group.items.map(async (item) => {
          const resolvedPhotos = (await Promise.all(item.photos.map(resolvePhotoKey))).filter(Boolean);
          return { ...item, photos: resolvedPhotos };
        })
      );
      return { ...group, items };
    })
  );
  return { ...mergedData, groups };
};

// Same up-front data-URI resolution as resolveExportPhotoUrls above, for the tabular export's
// single-photo-per-item shape (buildJobSelectionTableExportData) instead of a photos[] array.
export const resolveJobSelectionTablePhotoUrls = async (mergedData) => {
  const categoryTypes = await Promise.all(
    mergedData.categoryTypes.map(async (ct) => {
      const sections = await Promise.all(
        ct.sections.map(async (section) => {
          const items = await Promise.all(
            section.items.map(async (item) => ({ ...item, photo: await resolvePhotoKey(item.photo) }))
          );
          return { ...section, items };
        })
      );
      return { ...ct, sections };
    })
  );
  return { ...mergedData, categoryTypes };
};

const waitForImagesToLoad = (root) => {
  const imgs = Array.from(root.querySelectorAll("img"));
  return Promise.all(
    imgs.map((img) => {
      if (img.complete) return Promise.resolve();
      return new Promise((resolve) => {
        img.addEventListener("load", resolve, { once: true });
        img.addEventListener("error", resolve, { once: true });
      });
    })
  );
};

// Standard PowerPoint 16:9 slide size (13.333in x 7.5in @ 96dpi), matching the reference decks
// exactly. Each .pdf-slide in TemplatePdfPrintLayout is authored to this exact pixel size, so
// slicing the rasterized canvas into per-page chunks needs no boundary-detection heuristics —
// every slide is already exactly one page tall.
const SLIDE_WIDTH_PX = 1280;
const SLIDE_HEIGHT_PX = 720;
const SLIDE_WIDTH_IN = SLIDE_WIDTH_PX / 96;
const SLIDE_HEIGHT_IN = SLIDE_HEIGHT_PX / 96;

const generateSlideDeckPdf = (jsPDF, canvas, slideCount) => {
  const scale = canvas.width / SLIDE_WIDTH_PX;
  const sliceHeightPx = SLIDE_HEIGHT_PX * scale;

  const pdf = new jsPDF({ orientation: "landscape", unit: "in", format: [SLIDE_WIDTH_IN, SLIDE_HEIGHT_IN] });

  for (let i = 0; i < slideCount; i++) {
    const pageCanvas = document.createElement("canvas");
    pageCanvas.width = canvas.width;
    pageCanvas.height = Math.round(sliceHeightPx);

    const ctx = pageCanvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
    ctx.drawImage(
      canvas,
      0, Math.round(i * sliceHeightPx), canvas.width, Math.round(sliceHeightPx),
      0, 0, pageCanvas.width, pageCanvas.height
    );

    const imgData = pageCanvas.toDataURL("image/png");
    if (i > 0) pdf.addPage([SLIDE_WIDTH_IN, SLIDE_HEIGHT_IN], "landscape");
    pdf.addImage(imgData, "PNG", 0, 0, SLIDE_WIDTH_IN, SLIDE_HEIGHT_IN, "", "FAST");
  }

  return pdf;
};

// A4 portrait page (8.27in x 11.69in @ 96dpi), used only by the tabular job-selection export
// (JobSelectionTablePrintLayout) — distinct from SLIDE_WIDTH_PX/SLIDE_HEIGHT_PX's 16:9 slide deck.
// Must stay in sync with that component's own PAGE_WIDTH/PAGE_HEIGHT (same "no boundary-detection
// heuristics" reasoning as the slide deck: every .pdf-page is authored to this exact pixel size).
const PAGE_WIDTH_PX = 794;
const PAGE_HEIGHT_PX = 1123;
const PAGE_WIDTH_IN = PAGE_WIDTH_PX / 96;
const PAGE_HEIGHT_IN = PAGE_HEIGHT_PX / 96;

// Max items per page to prevent overflow (since each item row has a fixed height).
const MAX_PAGE_HEIGHT = 880; // 1123px total minus header, footer, padding

// Turns {categoryType, sections}[] into the flat page list JobSelectionTablePrintLayout renders,
// dynamically splitting sections so we don't exceed MAX_PAGE_HEIGHT per page.
export const buildJobSelectionTablePages = (categoryTypes) => {
  const pages = [];
  (categoryTypes || []).forEach(({ categoryType, sections }) => {
    if (!sections || sections.length === 0) {
      pages.push({ categoryType, sections: [] });
      return;
    }

    let currentPageSections = [];
    let currentHeight = 0;

    sections.forEach((section) => {
      let remainingItems = [...section.items];
      const hasHeading = Boolean(section.category && section.category !== "Uncategorised" && section.category !== "Direct Options");
      const SECTION_HEADING_HEIGHT = hasHeading ? 28 : 0;

      while (remainingItems.length > 0) {
        let fitCount = 0;
        let heightToAddForSection = 0;

        // If this is the first time we're adding this section to the current page, add heading height
        const isNewSectionOnPage = !currentPageSections.find(s => s.category === section.category);
        const headingHeight = isNewSectionOnPage ? SECTION_HEADING_HEIGHT : 0;

        for (let i = 0; i < remainingItems.length; i++) {
           const item = remainingItems[i];
           const itemHeight = item.estimatedHeight || 120;

           if (currentHeight === 0 && i === 0) {
              // Always fit at least 1 item on an empty page, even if it exceeds max height
              fitCount = 1;
              heightToAddForSection += headingHeight + itemHeight;
              break;
           }

           const addedHeight = (i === 0 ? headingHeight : 0) + itemHeight;

           if (currentHeight + heightToAddForSection + addedHeight <= MAX_PAGE_HEIGHT) {
              fitCount++;
              heightToAddForSection += addedHeight;
           } else {
              break;
           }
        }

        if (fitCount === 0) {
          // Page is full
          pages.push({ categoryType, sections: currentPageSections });
          currentPageSections = [];
          currentHeight = 0;
          continue;
        }

        const itemsForThisPage = remainingItems.slice(0, fitCount);
        remainingItems = remainingItems.slice(fitCount);

        let existingSection = currentPageSections.find(s => s.category === section.category);
        if (existingSection) {
            existingSection.items.push(...itemsForThisPage);
        } else {
            currentPageSections.push({
              category: section.category,
              items: itemsForThisPage,
            });
        }

        currentHeight += heightToAddForSection;
      }
    });

    if (currentPageSections.length > 0) {
      pages.push({ categoryType, sections: currentPageSections });
    }
  });
  return pages;
};

const generateTablePdf = (jsPDF, canvas, pageCount) => {
  const scale = canvas.width / PAGE_WIDTH_PX;
  const sliceHeightPx = PAGE_HEIGHT_PX * scale;

  const pdf = new jsPDF({ orientation: "portrait", unit: "in", format: [PAGE_WIDTH_IN, PAGE_HEIGHT_IN] });

  for (let i = 0; i < pageCount; i++) {
    const pageCanvas = document.createElement("canvas");
    pageCanvas.width = canvas.width;
    pageCanvas.height = Math.round(sliceHeightPx);

    const ctx = pageCanvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
    ctx.drawImage(
      canvas,
      0, Math.round(i * sliceHeightPx), canvas.width, Math.round(sliceHeightPx),
      0, 0, pageCanvas.width, pageCanvas.height
    );

    const imgData = pageCanvas.toDataURL("image/png");
    if (i > 0) pdf.addPage([PAGE_WIDTH_IN, PAGE_HEIGHT_IN], "portrait");
    pdf.addImage(imgData, "PNG", 0, 0, PAGE_WIDTH_IN, PAGE_HEIGHT_IN, "", "FAST");
  }

  return pdf;
};

// Same rasterize -> paginate -> save as renderPrintElementToPdf below, but for the tabular export's
// fixed-size .pdf-page elements (portrait A4) instead of .pdf-slide (16:9 landscape).
const renderTablePrintElementToPdf = async ({ printElement, fileName, download = true }) => {
  if (!printElement) {
    throw new Error("Nothing to export.");
  }

  const pageCount = printElement.querySelectorAll(".pdf-page").length;
  if (pageCount === 0) {
    throw new Error("Nothing to export.");
  }

  await waitForImagesToLoad(printElement);

  const [html2canvasModule, jsPDFModule] = await Promise.all([
    import("html2canvas-pro"),
    import("jspdf"),
  ]);
  const html2canvasPro = html2canvasModule.default || html2canvasModule;
  const { jsPDF } = jsPDFModule;

  const canvas = await html2canvasPro(printElement, {
    scale: 2,
    useCORS: true,
    logging: false,
    backgroundColor: "#ffffff",
  });

  const pdf = generateTablePdf(jsPDF, canvas, pageCount);
  if (download) pdf.save(fileName);
  return { blob: pdf.output("blob"), fileName };
};

export const exportJobSelectionsTableToPdf = async ({ title, printElement, download = true }) => {
  const safeName = (title || "Job-Selections").replace(/[^a-z0-9]+/gi, "-");
  return renderTablePrintElementToPdf({ printElement, fileName: `${safeName}-Selections.pdf`, download });
};

// Shared rasterize -> paginate -> save, used by both the Template export and the Selection
// Management catalog export below. Always returns the generated PDF as a Blob (plus the
// fileName it was built with) so callers that need the file itself — e.g. attaching it to the
// Email Client send flow — don't have to re-render; `download` (default true) additionally
// triggers the normal browser save-to-disk for the plain Export button flows.
const renderPrintElementToPdf = async ({ printElement, fileName, download = true }) => {
  if (!printElement) {
    throw new Error("Nothing to export.");
  }

  const slideCount = printElement.querySelectorAll(".pdf-slide").length;
  if (slideCount === 0) {
    throw new Error("Nothing to export.");
  }

  await waitForImagesToLoad(printElement);

  const [html2canvasModule, jsPDFModule] = await Promise.all([
    import("html2canvas-pro"),
    import("jspdf"),
  ]);
  const html2canvasPro = html2canvasModule.default || html2canvasModule;
  const { jsPDF } = jsPDFModule;

  const canvas = await html2canvasPro(printElement, {
    scale: 2,
    useCORS: true,
    logging: false,
    backgroundColor: "#ffffff",
  });

  const pdf = generateSlideDeckPdf(jsPDF, canvas, slideCount);
  if (download) pdf.save(fileName);
  return { blob: pdf.output("blob"), fileName };
};

export const exportTemplateToPdf = async ({ templateSummary, printElement }) => {
  const safeName = (templateSummary?.name || "Selection-Template").replace(/[^a-z0-9]+/gi, "-");
  await renderPrintElementToPdf({ printElement, fileName: `${safeName}-Selections.pdf` });
};

export const exportCatalogToPdf = async ({ title, printElement }) => {
  const safeName = (title || "Selection-Catalog").replace(/[^a-z0-9]+/gi, "-");
  await renderPrintElementToPdf({ printElement, fileName: `${safeName}.pdf` });
};